import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { useRecoilValue } from 'recoil';
import { datadogLogs } from '@datadog/browser-logs';
import { useHistory } from 'react-router-dom';

import { CURRENT_USER } from 'graphql/queries/user';
import { USER_ROUTES } from 'graphql/queries/routes';
import {
  CHECK_2FA,
  RESEND_2FA,
  RESEND_2FA_BY_EMAIL
} from 'graphql/mutations/user';
import { Button, ToastBody } from 'components';
import { validateNumbers } from 'validations';

import {
  NextIconStyled,
  SubTitleStyled,
  MessageStyled,
  ButtonGroupStyled,
  CheckboxStyled,
  TextButtonStyled,
  ArrowLinkStyled,
  InputStyled
} from './user-2facode.styles';
import { phoneNumberAtom, twoFaMethodAtom } from '../state';
import { TWO_FA_METHODS, GRAPHQL_ERROR_TYPE, STORAGE_KEY } from 'shared/enums';
import { User2FaMethod } from '.';
import obscurePhoneNumber from 'shared/helpers/obscurePhoneNumber';

export default ({ token, t }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [checkboxRemind, setCheckboxRemind] = useState(false);
  const history = useHistory();
  const [error, setError] = useState('');
  const [check2fa] = useMutation(CHECK_2FA);
  const [resend2fa] = useMutation(RESEND_2FA);
  const [resend2faByEmail] = useMutation(RESEND_2FA_BY_EMAIL);
  const phoneNumber = useRecoilValue(phoneNumberAtom);
  const twoFaMethodRecoil = useRecoilValue(twoFaMethodAtom);
  const [showAnotherWay, setShowAnotherWay] = useState(false);
  const [twoFaMethod, setTwoFaMethod] = useState(twoFaMethodRecoil);

  const message = {
    sms: `${t?.('signin_sms_message')} ${obscurePhoneNumber(phoneNumber)}.`,
    call: `${t?.('signin_sms_call')} ${obscurePhoneNumber(phoneNumber)}.`,
    email: t?.('signin_email_message')
  };

  const validateSubmit = ({ verificationCode, t }) => {
    return (
      validateNumbers({
        field: verificationCode,
        fieldName: 'verificationCode',
        fieldMessage: t?.('input_verification_code_message')
      })?.[0]?.message || ''
    );
  };

  const handleSubmit = async () => {
    const hasError = validateSubmit({ verificationCode, t });
    if (hasError) {
      datadogLogs.logger.warn(
        'Insights authentication error (2fa)',
        {},
        hasError
      );
      return setError(hasError);
    }

    await check2fa({
      variables: {
        code: verificationCode,
        persist: checkboxRemind,
        login: true,
        token
      },
      onCompleted: data => {
        if (data) {
          // remove passthrough if set from token login
          localStorage.removeItem('passthrough');
          localStorage.setItem(STORAGE_KEY.LOGIN_TOKEN, data?.check2fa);

          // when a user is redirected to /signin, they sometimes get stuck on the 2fa page
          // this is a temporary solution to fix the issue
          history.push({
            pathname: '/',
            state: {
              isLogged: true
            }
          });
        }
      },
      onError: error => {
        if (
          error?.graphQLErrors[0]?.message?.type === GRAPHQL_ERROR_TYPE.INVALID
        ) {
          setError(t?.('input_verification_code_error'));
        } else {
          setError(t?.('common_network_error'));
        }
        datadogLogs.logger.warn(
          'Insights authentication error (2fa)',
          {},
          error
        );
      },
      refetchQueries: [{ query: CURRENT_USER }, { query: USER_ROUTES }]
    });
  };

  const handleResendByEmail = async () => {
    await resend2faByEmail({
      variables: {
        token
      },
      onCompleted: () => {
        toast.success(
          <ToastBody
            data-pendoid="signin_send_2fa_email"
            category="success"
            title={t?.('signin_2fa_email_title')}
            text={t?.('signin_2fa_email_message')}
          />
        );
      },
      onError: error => {
        datadogLogs.logger.error('Insights resend 2fa email error', {}, error);
        setError(t?.('common_network_error'));
      }
    });
  };

  const handleResend2fa = async () => {
    if (twoFaMethod === TWO_FA_METHODS.EMAIL) {
      handleResendByEmail();
    } else {
      await resend2fa({
        variables: {
          token,
          overrideMethod: twoFaMethod
        },
        onCompleted: () => {
          toast.success(
            <ToastBody
              category="success"
              title={t?.('signup_confirmation_title')}
              text={t?.('signin_2fa_email_title')}
            />
          );
        },
        onError: error => {
          datadogLogs.logger.error(
            'Insights resend 2fa twillo error',
            {},
            error
          );
          toast.error(
            <ToastBody
              data-pendoid="signin_invalid_2fa_phone"
              category="error"
              title={t?.('signin_invalid_2fa_phone_title')}
              text={t?.('signin_invalid_2fa_phone_message')}
            />
          );
        }
      });
    }
  };

  const handleAnotherWayCallback = newMethod => {
    setShowAnotherWay(false);
    setTwoFaMethod(newMethod);
  };

  return (
    <>
      {showAnotherWay ? (
        <User2FaMethod
          t={t}
          token={token}
          handleCallback={handleAnotherWayCallback}
        />
      ) : (
        <>
          <SubTitleStyled data-pendoid="account_security_2fa_modal_verification_code_message2">
            <div>{message[twoFaMethod]}</div>
            <div>
              {t?.('account_security_2fa_modal_verification_code_message2')}
            </div>
          </SubTitleStyled>
          <form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <InputStyled
              data-pendoid="input_verification_code"
              label={t?.('input_verification_code')}
              name="verificationCode"
              value={verificationCode}
              handleChange={e => {
                if (error) {
                  setError('');
                }
                setVerificationCode(e.target.value);
              }}
              error={Boolean(error)}
              errorMessage={error}
              required={true}
              errorPlaceholder={true}
            />
            <CheckboxStyled
              data-pendoid="signin_remember"
              handleClick={() => setCheckboxRemind(!checkboxRemind)}
              name="checkbox-remind"
              text={t?.('signin_remember')}
              checked={checkboxRemind}
            />
            <MessageStyled
              customMargin="12px"
              data-pendoid="common_verification_code_question"
            >
              {t?.('common_verification_code_question')}
            </MessageStyled>
            <TextButtonStyled
              data-pendoid="common_verification_code_resend"
              customMargin="12px"
              handleClick={handleResend2fa}
            >
              {t?.('common_verification_code_resend')} <ArrowLinkStyled />
            </TextButtonStyled>
            {twoFaMethodRecoil !== TWO_FA_METHODS.EMAIL && (
              <TextButtonStyled
                data-pendoid="common_try_another_way"
                handleClick={() => setShowAnotherWay(true)}
              >
                {t?.('common_try_another_way')} <ArrowLinkStyled />
              </TextButtonStyled>
            )}
            <ButtonGroupStyled data-pendoid="buttons_next">
              <Button type="submit" label="Next">
                {t?.('buttons_next')} <NextIconStyled />
              </Button>
            </ButtonGroupStyled>
          </form>
        </>
      )}
    </>
  );
};
